/* src/components/CurrencySwitcher.tsx */
import React, { Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useCurrency, SUPPORTED_CURRENCIES } from "contexts/CurrencyContext";

function cn(...cls: (string | false | undefined)[]) {
  return cls.filter(Boolean).join(" ");
}

const CurrencySwitcher: React.FC<{ className?: string }> = ({ className = "" }) => {
  const { currency, setCurrency } = useCurrency();

  return (
    <Popover as="div" className={cn("relative", className)}>
      {({ open, close }) => (
        <>
          {/* ── Trigger ───────────────────────────────────────── */}
          <Popover.Button
            aria-label="Select currency"
            className={cn(
              // responsive padding / text
              "inline-flex items-center gap-1 rounded-full font-medium whitespace-nowrap",
              "px-2.5 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm lg:px-4",
              // border / bg
              "border shadow-sm bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200",
              "dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-700",
              // focus ring
              "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            )}
          >
            {/* Globe appears ≥ sm; hidden on narrower screens */}
            <span className="hidden sm:inline">🌍</span>
            {currency}
            <ChevronDownIcon
              className={cn(
                "w-3 h-3 sm:w-4 sm:h-4 transition-transform", // chevron hidden < sm
                open && "rotate-180"
              )}
            />
          </Popover.Button>

          {/* ── Dropdown panel ───────────────────────────────── */}
          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Popover.Panel
              static
              className={cn(
                "absolute right-0 mt-2 origin-top-right z-50 rounded-xl",
                "w-24 sm:w-28",
                "bg-white dark:bg-slate-800 shadow-lg ring-1 ring-black/5",
                "border border-slate-200 dark:border-slate-600 focus:outline-none"
              )}
            >
              {SUPPORTED_CURRENCIES.map((c) => (
                <button
                  key={c}
                  onClick={() => {
                    setCurrency(c);
                    close();
                  }}
                  className={cn(
                    "block w-full text-left px-4 py-2 text-xs sm:text-sm",
                    "hover:bg-slate-100 dark:hover:bg-slate-700",
                    currency === c
                      ? "font-semibold text-primary-600 dark:text-primary-400"
                      : "text-slate-700 dark:text-slate-200"
                  )}
                >
                  {c}
                </button>
              ))}
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default CurrencySwitcher;
